import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const VisitList = () => {
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch staff list
        const staffResponse = await fetch(`${BASE_URL}staff`);
        const staffData = await staffResponse.json();

        let staffListData = [];
        if (staffData.status === 1 && staffData.data) {
          staffListData = staffData.data;
          setStaffList(staffListData);
        }

        // Fetch visits
        const visitsResponse = await fetch(`${BASE_URL}visits`);
        const visitsData = await visitsResponse.json();

        if (visitsData.status === 1 && visitsData.data) {
          // Map staff names to visits
          const visitsWithStaffNames = visitsData.data.map((visit) => {
            const staff = staffListData.find((s) => s.id == visit.staff_id);
            return {
              ...visit,
              staff_name: staff ? staff.name : `Staff ID: ${visit.staff_id}`,
            };
          });

          setVisits(visitsWithStaffNames);
          setFilteredVisits(visitsWithStaffNames);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get staff name by ID
  const getStaffName = (staffId) => {
    if (!staffId || !staffList.length) return "N/A";
    const staff = staffList.find((s) => s.id == staffId);
    return staff ? staff.name : `Staff ID: ${staffId}`;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    filterVisits(term, startDate, endDate);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    filterVisits(searchTerm, start, end);
  };

  // Filter visits by search term and date range
  const filterVisits = (term, start, end) => {
    const termLower = term.toLowerCase();
    let filtered = visits.filter((visit) => {
      // Search in multiple fields including staff name
      const matchesSearch =
        visit.customer_name?.toLowerCase().includes(termLower) ||
        visit.contact_person_name?.toLowerCase().includes(termLower) ||
        visit.contact_number?.toLowerCase().includes(termLower) ||
        visit.description?.toLowerCase().includes(termLower) ||
        visit.staff_name?.toLowerCase().includes(termLower);

      if (visit.date) {
        const visitDate = new Date(visit.date);
        const matchesDateRange =
          (!start || visitDate >= new Date(start)) &&
          (!end || visitDate <= new Date(end));
        return matchesSearch && matchesDateRange;
      }

      return matchesSearch;
    });

    setFilteredVisits(filtered);
  };

  const handleView = (id) => {
    navigate(`/ViewVisit/${id}`);
  };

  const handleAdd = () => {
    navigate("/AddVisit");
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this visit?")) {
      fetch(`${BASE_URL}visits/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 1) {
            setVisits(visits.filter((visit) => visit.id !== id));
            setFilteredVisits(
              filteredVisits.filter((visit) => visit.id !== id)
            );
          }
        })
        .catch((error) => console.error("Error deleting visit:", error));
    }
  };

  // Export filteredVisits to PDF with staff name
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Outdoor Visit List", 14, 10);

    const tableData = filteredVisits.map((visit) => [
      visit.staff_name || getStaffName(visit.staff_id) || "N/A",
      visit.customer_name || "N/A",
      visit.contact_person_name || "N/A",
      visit.contact_number || "N/A",
      visit.description || "N/A",
      formatDate(visit.date),
    ]);

    doc.autoTable({
      head: [
        [
          "Staff Name",
          "Customer",
          "Contact Person",
          "Phone",
          "Description",
          "Date",
        ],
      ],
      body: tableData,
    });

    doc.save("outdoor_visit_list.pdf");
  };

  // Export filteredVisits to Excel with staff name
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredVisits.map((visit) => ({
        "Staff Name": visit.staff_name || getStaffName(visit.staff_id) || "N/A",
        Customer: visit.customer_name || "N/A",
        "Contact Person": visit.contact_person_name || "N/A",
        Phone: visit.contact_number || "N/A",
        Description: visit.description || "N/A",
        Date: formatDate(visit.date),
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Outdoor Visits");
    XLSX.writeFile(workbook, "outdoor_visit_list.xlsx");
  };

  if (isLoading) {
    return (
      <div id="main">
        <div className="page-heading">
          <h3>Outdoor Visit List</h3>
        </div>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-3">Loading visits...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Outdoor Visit List</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6">
                    <h5>Total Visits: {filteredVisits.length}</h5>
                  </div>
                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-primary me-2"
                      onClick={handleAdd}
                    >
                      Add Visit
                    </button>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleExportExcel}
                    >
                      Export to Excel
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Search and Date Filters */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label htmlFor="searchTerm" className="form-label">
                      Search
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search by staff, customer, contact person, phone, or description"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) =>
                        handleDateChange(e.target.value, endDate)
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) =>
                        handleDateChange(startDate, e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDateChange(startDate, endDate)}
                    >
                      Apply Date Filter
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>Staff Name</th>
                        <th>Firm Name</th>
                        <th>Contact Person</th>
                        <th>Phone</th>
                        <th>Description</th>
                        <th>Date</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVisits.length > 0 ? (
                        filteredVisits.map((visit) => (
                          <tr key={visit.id}>
                            <td>
                              {visit.staff_name ||
                                getStaffName(visit.staff_id) ||
                                "N/A"}
                            </td>
                            <td>{visit.customer_name || "N/A"}</td>
                            <td>{visit.contact_person_name || "N/A"}</td>
                            <td>{visit.contact_number || "N/A"}</td>
                            <td>{visit.description || "N/A"}</td>
                            <td>{formatDate(visit.date)}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleView(visit.id)}
                              >
                                View
                              </button>
                              <button
                                className="btn btn-danger ms-2"
                                onClick={() => handleDelete(visit.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center">
                            No visits found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisitList;
